import Check from "lucide-react/dist/esm/icons/check";
import Copy from "lucide-react/dist/esm/icons/copy";
import LogOut from "lucide-react/dist/esm/icons/log-out";
import Share2 from "lucide-react/dist/esm/icons/share-2";
import Users from "lucide-react/dist/esm/icons/users";
import type { RoomView } from "../../game";
import { Countdown } from "./countdown";

interface WaitingRoomProps {
  room: RoomView;
  onReady: () => void;
  onLeave: () => void;
  onCopied: (message: string) => void;
}

export function WaitingRoom({ room, onReady, onLeave, onCopied }: WaitingRoomProps) {
  const self = room.players.find((player) => player.id === room.selfId);
  const rival = room.players.find((player) => player.id !== room.selfId);
  const seriesActive = (self?.wins ?? 0) + (rival?.wins ?? 0) > 0;
  const inviteUrl = new URL(window.location.href);
  inviteUrl.searchParams.set("sala", room.code);

  async function copyInvite(): Promise<void> {
    try {
      await navigator.clipboard.writeText(inviteUrl.toString());
      onCopied("Enlace copiado. Mandáselo a tu rival.");
    } catch {
      onCopied(`Compartí este código: ${room.code}`);
    }
  }

  async function shareInvite(): Promise<void> {
    if (navigator.share) {
      try {
        await navigator.share({
          title: "Jugá CHIN conmigo",
          text: `Entrá a mi mesa de CHIN con el código ${room.code}`,
          url: inviteUrl.toString(),
        });
        return;
      } catch (error) {
        if (error instanceof DOMException && error.name === "AbortError") return;
      }
    }
    await copyInvite();
  }

  return (
    <main className="waiting-shell">
      <header className="room-header">
        <span className="wordmark wordmark--small">CHIN!</span>
        <button className="icon-button" type="button" onClick={onLeave} aria-label="Salir de la mesa">
          <LogOut aria-hidden="true" size={21} />
        </button>
      </header>

      <section className="waiting-content">
        <p className="eyebrow">
          <Users aria-hidden="true" size={15} /> Mesa privada
          {room.settings.decks === 2 ? <span className="deck-badge">2 mazos</span> : null}
          {seriesActive ? <span className="deck-badge">Serie {self?.wins ?? 0}–{rival?.wins ?? 0}</span> : null}
        </p>
        <h1>{rival ? "Los dos están dentro" : "Invitá a tu rival"}</h1>
        <p>{rival ? "Cuando ambos estén listos, arrancamos la cuenta regresiva." : "Compartí este código. La mesa admite exactamente dos jugadores."}</p>

        <div className="room-code-block">
          <span>Código de mesa</span>
          <strong>{room.code}</strong>
          <div>
            <button type="button" className="secondary-button" onClick={copyInvite}>
              <Copy aria-hidden="true" size={18} /> Copiar
            </button>
            <button type="button" className="secondary-button" onClick={shareInvite}>
              <Share2 aria-hidden="true" size={18} /> Compartir
            </button>
          </div>
        </div>

        <div className="player-seats">
          {[self, rival].map((player, index) => (
            <div className={`player-seat ${player?.ready ? "is-ready" : ""}`} key={player?.id ?? `empty-${index}`}>
              <span className="player-avatar">{player?.name.charAt(0).toUpperCase() ?? "?"}</span>
              <span>
                <strong>{player?.name ?? "Esperando…"}</strong>
                <small>{player ? player.id === room.selfId ? "Vos" : "Rival" : "Lugar libre"}</small>
              </span>
              {player?.ready ? <Check className="ready-check" aria-label="Listo" size={21} /> : <i className="seat-status" aria-hidden="true" />}
            </div>
          ))}
        </div>

        <div className="waiting-rules" aria-label="Resumen de reglas">
          <span><b>1</b> Jugá ±1</span>
          <span><b>2</b> Vacía tu mazo</span>
          <span><b>3</b> Cantá CHIN primero</span>
        </div>

        <button className="primary-button ready-button" type="button" onClick={onReady} disabled={!rival || self?.ready || room.phase === "countdown"}>
          {self?.ready ? "Esperando al rival…" : rival ? "Estoy listo" : "Falta tu rival"}
        </button>
      </section>

      {room.phase === "countdown" && room.countdownEndsAt ? <Countdown endsAt={room.countdownEndsAt} /> : null}
    </main>
  );
}
